import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Warning, Lightning, Fire, Drop, Thermometer, Wind, Mountains, Snowflake } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICON = {
  WILDFIRE: Fire,
  STORM: Lightning,
  THUNDERSTORM: Lightning,
  VOLCANO: Mountains,
  FLOOD: Drop,
  "HEAVY RAIN": Drop,
  EARTHQUAKE: Mountains,
  "EXTREME HEAT": Thermometer,
  "HEAT WAVE": Thermometer,
  "EXTREME COLD": Snowflake,
  SNOW: Snowflake,
  "HIGH WIND": Wind,
  "WIND ALERT": Wind,
  DROUGHT: Thermometer,
  ICE: Snowflake,
};

export default function NewsTicker({ userLat, userLon }) {
  const [alerts, setAlerts] = useState([]);
  const tickerRef = useRef(null);

  useEffect(() => {
    const doFetch = async () => {
      try {
        const params = {};
        if (userLat != null) params.lat = userLat;
        if (userLon != null) params.lon = userLon;
        const res = await axios.get(`${API}/ticker/alerts`, { params, withCredentials: true });
        setAlerts(res.data?.alerts || []);
      } catch {
        /* silent */
      }
    };
    doFetch();
    const interval = setInterval(doFetch, 300000);
    return () => clearInterval(interval);
  }, [userLat, userLon]);

  if (alerts.length === 0) return null;

  // Build the ticker text items
  const tickerItems = alerts.map((a, i) => {
    const Icon = CATEGORY_ICON[a.category] || Warning;
    const isHappening = a.type === "happening";
    return (
      <span key={i} className="inline-flex items-center gap-2 mr-12 whitespace-nowrap">
        {/* Severity dot */}
        <span
          className={`w-2 h-2 rounded-full shrink-0 ${
            a.severity === "critical" ? "animate-pulse" : ""
          }`}
          style={{ backgroundColor: a.color }}
        />
        {/* Category badge */}
        <span
          className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
          style={{ backgroundColor: a.color + "25", color: a.color }}
        >
          <Icon size={10} weight="bold" className="inline mr-0.5 -mt-0.5" />
          {a.category}
        </span>
        {/* Type tag */}
        <span
          className={`text-[9px] uppercase tracking-wider px-1 py-0.5 rounded font-bold ${
            isHappening
              ? "bg-red-500/20 text-red-400"
              : "bg-amber-500/20 text-amber-400"
          }`}
        >
          {isHappening ? "LIVE" : "FORECAST"}
        </span>
        {/* Title */}
        <span className="text-xs text-stone-300 font-body">{a.title}</span>
        {/* Source */}
        <span className="text-[9px] text-stone-600">({a.source})</span>
      </span>
    );
  });

  // Duplicate for seamless loop
  const allItems = [...tickerItems, ...tickerItems.map((item, i) => (
    <span key={`dup-${i}`} className="inline-flex items-center gap-2 mr-12 whitespace-nowrap">
      {item.props.children}
    </span>
  ))];

  return (
    <div
      className="relative w-full overflow-hidden bg-stone-950/95 border-b border-stone-800 z-[999]"
      data-testid="news-ticker"
    >
      {/* Left label */}
      <div className="absolute left-0 top-0 bottom-0 z-10 flex items-center bg-gradient-to-r from-red-600 to-red-700 px-3">
        <Warning size={12} weight="bold" className="text-white mr-1.5" />
        <span className="text-[10px] text-white font-heading font-bold uppercase tracking-wider">
          ALERTS
        </span>
      </div>

      {/* Scrolling content */}
      <div className="pl-24 h-8 flex items-center">
        <div
          ref={tickerRef}
          className="inline-flex items-center ticker-scroll"
          style={{
            animation: `ticker-scroll ${Math.max(30, alerts.length * 8)}s linear infinite`,
          }}
        >
          {allItems}
        </div>
      </div>

      {/* Right fade */}
      <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-stone-950 to-transparent pointer-events-none" />

      <style>{`
        @keyframes ticker-scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .ticker-scroll:hover {
          animation-play-state: paused;
        }
      `}</style>
    </div>
  );
}
